import { createFileRoute, Link } from "@tanstack/react-router";
import { JsonLd } from "@/components/json-ld";
import { canonicalLink, seo } from "@/utils/seo";
import { breadcrumbJsonLd } from "@/utils/structured-data";

export const Route = createFileRoute("/legal/terms")({
  head: () => ({
    meta: [
      ...seo({
        title: "Terms of Service - Setups by SZESE",
        description:
          "Read the Setups by SZESE terms of service. Understand your rights and responsibilities when using our iRacing setup platform.",
        canonical: "https://setupsbyszese.com/legal/terms",
      }),
    ],
    links: [canonicalLink("https://setupsbyszese.com/legal/terms")],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <main className="min-h-screen bg-background p-6">
      <JsonLd
        data={breadcrumbJsonLd([
          { name: "Home", url: "https://setupsbyszese.com" },
          { name: "Terms of Service", url: "https://setupsbyszese.com/legal/terms" },
        ])}
      />
      <div className="mx-auto max-w-3xl">
        <div className="mb-8">
          <Link
            to="/"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            &larr; Back to Setups by SZESE
          </Link>
        </div>

        <h1 className="text-3xl font-bold mb-2">Terms of Service</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: March 15, 2026</p>

        <div className="prose prose-invert prose-sm max-w-none space-y-6 text-muted-foreground [&_h2]:text-foreground [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-8 [&_h2]:mb-3 [&_h3]:text-foreground [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-2 [&_strong]:text-foreground [&_a]:text-primary [&_a]:underline [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-1">
          <p>
            These Terms of Service (&quot;Terms&quot;) govern your use of the Setups by SZESE
            website at <a href="https://setupsbyszese.com">setupsbyszese.com</a> (the
            &quot;Service&quot;), operated by SZESE E-Sport, the sim racing team of Sz&eacute;chenyi
            Istv&aacute;n University, Gy&#x151;r, Hungary (&quot;we&quot;, &quot;us&quot;,
            &quot;our&quot;). By creating an account or using the Service, you agree to these Terms.
          </p>

          <h2>1. The Service</h2>
          <p>
            Setups by SZESE is a platform where professional sim racing drivers share iRacing car
            setup files, replay files, and telemetry data with subscribers. We provide setup files
            for use within the iRacing simulation platform.
          </p>

          <h2>2. Accounts</h2>
          <ul>
            <li>You must be at least 16 years old to create an account.</li>
            <li>
              You are responsible for keeping your account credentials secure. Do not share your
              login with others.
            </li>
            <li>
              You must provide accurate information when creating your account. We may suspend
              accounts with clearly false information.
            </li>
            <li>
              You can delete your account at any time via Settings &gt; Danger Zone. This
              permanently removes your profile and personal data.
            </li>
          </ul>

          <h2>3. Subscription Tiers</h2>
          <p>The Service offers three access tiers:</p>
          <ul>
            <li>
              <strong>Free:</strong> Access to a limited selection of sample setups each week.
            </li>
            <li>
              <strong>Plus ($8/month):</strong> Access to all weekly setups for your chosen series.
              You may change your selected series once every 30 days.
            </li>
            <li>
              <strong>Premium ($15/month):</strong> Access to all setups across all series, plus
              replay files (.rpy) and telemetry data (.ibt).
            </li>
          </ul>
          <p>
            Prices are in USD and may be subject to applicable taxes. We reserve the right to change
            pricing with 30 days&apos; notice to existing subscribers.
          </p>

          <h2>4. Billing &amp; Cancellation</h2>
          <ul>
            <li>
              Subscriptions are billed monthly through{" "}
              <a href="https://polar.sh" target="_blank" rel="noopener noreferrer">
                Polar
              </a>
              , our payment processor.
            </li>
            <li>
              You may cancel your subscription at any time. Cancellation takes effect at the end of
              your current billing period &mdash; you retain access until then.
            </li>
            <li>
              You may resume a canceled subscription before the billing period ends to continue
              without interruption.
            </li>
            <li>
              We do not offer refunds for partial months. If you believe you were charged in error,
              contact us at <a href="mailto:support@setupsbyszese.com">support@setupsbyszese.com</a>
              .
            </li>
          </ul>

          <h2>5. Setup Files &amp; Content</h2>

          <h3>Ownership</h3>
          <p>
            Setup files, replay files, and telemetry data uploaded to the Service
            (&quot;Content&quot;) remain the intellectual property of SZESE E-Sport and its drivers.
            Subscribers receive a limited, non-exclusive, non-transferable license to use downloaded
            Content for personal use within iRacing.
          </p>

          <h3>Permitted Use</h3>
          <ul>
            <li>Download and use setups in your own iRacing sessions.</li>
            <li>Modify downloaded setups for your personal use.</li>
          </ul>

          <h3>Prohibited Use</h3>
          <ul>
            <li>
              <strong>Redistribution:</strong> You may not share, redistribute, resell, or publicly
              post downloaded Content on any other platform, website, forum, or file-sharing
              service.
            </li>
            <li>
              <strong>Commercial use:</strong> You may not use downloaded Content for commercial
              purposes, including selling modified versions.
            </li>
            <li>
              <strong>Account sharing:</strong> Your subscription is for your personal use only. Do
              not share your account credentials or downloaded files with others.
            </li>
          </ul>
          <p>
            Violation of these restrictions may result in immediate account termination without
            refund.
          </p>

          <h2>6. Driver Contributions</h2>
          <p>If you have been assigned the Driver role:</p>
          <ul>
            <li>
              You grant SZESE E-Sport a worldwide, royalty-free license to distribute the setups,
              replays, and telemetry you upload through the Service.
            </li>
            <li>
              You represent that you have the right to share the content you upload and that it does
              not infringe on any third-party rights.
            </li>
            <li>
              Uploaded content goes through an approval process before being published to
              subscribers.
            </li>
            <li>
              If you leave the team, your previously uploaded and approved setups remain available
              on the platform. You may request removal by contacting us.
            </li>
          </ul>

          <h2>7. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>
              Use automated tools, bots, or scripts to bulk-download Content or scrape the Service.
            </li>
            <li>Circumvent subscription tier restrictions or access controls.</li>
            <li>Interfere with the Service&apos;s operation or other users&apos; access.</li>
            <li>Impersonate others or create accounts with false identities.</li>
            <li>Upload malicious files or content that could harm other users or the platform.</li>
          </ul>

          <h2>8. Disclaimers</h2>
          <ul>
            <li>
              The Service is provided &quot;as is&quot; without warranties of any kind. We do not
              guarantee that setups will improve your iRacing performance.
            </li>
            <li>
              Setup files are created by our drivers based on their expertise. Results may vary
              depending on your driving style, hardware, and iRacing settings.
            </li>
            <li>
              We are not affiliated with or endorsed by iRacing.com Motorsport Simulations, LLC.
              iRacing is a trademark of iRacing.com.
            </li>
            <li>
              We do not guarantee uninterrupted access to the Service. Maintenance, updates, or
              technical issues may cause temporary downtime.
            </li>
          </ul>

          <h2>9. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by applicable law, SZESE E-Sport and Sz&eacute;chenyi
            Istv&aacute;n University shall not be liable for any indirect, incidental, special, or
            consequential damages arising from your use of the Service. Our total liability is
            limited to the amount you paid for the Service in the 12 months preceding the claim.
          </p>

          <h2>10. Termination</h2>
          <ul>
            <li>You may terminate your account at any time by deleting it in Settings.</li>
            <li>
              We may suspend or terminate your account if you violate these Terms, with or without
              notice depending on the severity of the violation.
            </li>
            <li>
              Upon termination, your access to the Service and all downloaded Content licenses are
              revoked. You should delete any downloaded Content.
            </li>
          </ul>

          <h2>11. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. We will notify registered users of material
            changes via email or an in-app notification at least 14 days before they take effect.
            Continued use of the Service after changes take effect constitutes acceptance of the
            updated Terms.
          </p>

          <h2>12. Governing Law</h2>
          <p>
            These Terms are governed by the laws of Hungary. Any disputes shall be subject to the
            exclusive jurisdiction of the courts of Gy&#x151;r, Hungary, unless EU consumer
            protection regulations provide otherwise.
          </p>

          <h2>13. Contact</h2>
          <p>
            For questions about these Terms, contact us at{" "}
            <a href="mailto:support@setupsbyszese.com">support@setupsbyszese.com</a>.
          </p>
          <p>
            SZESE E-Sport
            <br />
            Sz&eacute;chenyi Istv&aacute;n University
            <br />
            Gy&#x151;r, Hungary
          </p>
        </div>
      </div>
    </main>
  );
}
